import { useState } from "react";
import Avatar from "../common/Avatar";
import BottomSheet from "../common/BottomSheet";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { lawyerService } from "../../services/api";
import useTranslate from "../../hooks/useTranslate";
import { CloseIcon, ChevronDownIcon, CheckmarkIcon } from "./v2/icons";
import { LAW_SCRIM } from "./v2/tokens";

const TOPICS = [
  "Contract review",
  "Transfer dispute",
  "Work permit",
  "Regulatory advice",
  "Other",
];

// Contact-request modal — rebuilt 1:1 from design handoff artboards 1g
// (desktop form modal), 1h (success state) and 1n (mobile bottom sheet).
// Logic untouched (D-010): submitting creates a PENDING request the lawyer
// accepts or declines — NOT an instant chat. The 1h "Go to chats" primary
// button is NOT wired (no existing logic behind it — deliberately no inbox
// navigation on send); recorded as a FUNCTION-TODO row in parity/PKG-2.md.
const ContactLawyerModal = ({ lawyer, onClose, onSent, t: tProp }) => {
  const { t: tHook } = useTranslate();
  const t = tProp || tHook;
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { pending } | { error }

  if (!lawyer) return null;

  const isSuccess = !!(result && !result.error);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!message.trim() || submitting) return;
    setSubmitting(true);
    // D-010: this creates a PENDING request (accept/decline by the lawyer), not
    // an instant chat. No inbox navigation on send.
    const res = await lawyerService.createRequest({
      lawyerAccountId: lawyer.account_id,
      topic: topic || undefined,
      message: message.trim(),
    });
    setSubmitting(false);
    if (res.success) {
      setResult({ pending: true });
      if (onSent) onSent(lawyer); // let the caller flip the card to "Request pending"
    } else {
      // Surface the error inline; keep the form so the user can retry.
      setResult({ error: res.error || t("Failed to send request") });
    }
  };

  // ── Success state (1h) — request sent, awaiting the lawyer's decision.
  // Copy per Marin's B20 decision (2026-07-23): artboard-flavored but
  // function-accurate for the D-010 pending flow — "If {name} accepts, you'll
  // chat in your TransferPitch chats." with the lawyer's first name
  // interpolated ({name} placeholder survives translation, then is replaced).
  const firstName = (lawyer.name || "").trim().split(/\s+/)[0] || lawyer.name;
  const successCopy = t(
    "If {name} accepts, you'll chat in your TransferPitch chats."
  ).replace("{name}", firstName);
  const successBody = (
    <div className="flex flex-col items-center text-center gap-[6px] py-2">
      <div className="w-14 h-14 rounded-full bg-[#E7F8EF] flex items-center justify-center mb-2">
        <CheckmarkIcon size={26} strokeWidth={2.5} className="text-[#12B76A]" />
      </div>
      <h3 className="text-[17px] font-bold text-[#101828]">{t("Request sent")}</h3>
      <p className="text-[13.5px] text-[#667085] leading-[1.5] max-w-[300px]">
        {successCopy}
      </p>
      <div className="flex gap-[9px] mt-4">
        <button
          type="button"
          onClick={onClose}
          className="bg-white text-[#344054] border border-[#DDE3EC] rounded-[9px] px-4 py-[9px] font-semibold text-[13px] cursor-pointer"
        >
          {t("Done")}
        </button>
      </div>
    </div>
  );

  // ── Shared form fields (1g desktop / 1n mobile geometry) ──
  const formFields = (
    <div className={`flex flex-col ${isMobile ? "gap-3" : "mt-4 gap-3"}`}>
      {/* Topic (optional) */}
      <div>
        <label className="block text-[12px] font-semibold text-[#344054] mb-[5px]">
          {t("Topic")} <span className="text-[#98A2B3] font-normal">({t("optional")})</span>
        </label>
        <div className="relative">
          <select
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className={`w-full appearance-none bg-white border border-[#DDE3EC] text-[13px] font-medium focus:outline-none focus:border-[#2283FF] ${
              isMobile ? "rounded-[10px] pl-[13px] py-[11px]" : "rounded-[9px] pl-3 py-[9px]"
            } pr-9 ${topic ? "text-[#101828]" : "text-[#98A2B3]"}`}
          >
            <option value="">{t("Select a topic")}</option>
            {TOPICS.map((tp) => (
              <option key={tp} value={tp}>
                {t(tp)}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#98A2B3]">
            <ChevronDownIcon size={12} />
          </span>
        </div>
      </div>

      {/* Message (required) */}
      <div>
        <label className="block text-[12px] font-semibold text-[#344054] mb-[5px]">
          {t("Message")}
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("Briefly describe your matter: country, deal type, timeline.")}
          className={`w-full resize-y border border-[#DDE3EC] text-[13px] leading-[1.5] text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:border-[#2283FF] ${
            isMobile ? "rounded-[10px] px-[13px] py-[11px] h-[84px]" : "rounded-[9px] px-3 py-[11px] h-[88px]"
          }`}
        />
      </div>

      {result?.error && (
        <p className="text-[13px] text-[#B42318]">{result.error}</p>
      )}
    </div>
  );

  // ── Mobile — artboard 1n: bottom sheet ──
  if (isMobile) {
    return (
      <BottomSheet
        open
        onClose={onClose}
        title={isSuccess ? t("Request sent") : `${t("Contact")} ${lawyer.name}`}
      >
        <div className="pb-4">
          {isSuccess ? (
            successBody
          ) : (
            <form onSubmit={handleSubmit}>
              <p className="text-[12px] text-[#667085] -mt-1 mb-[14px]">
                {t("Starts a request in your TransferPitch chats")}
              </p>
              {formFields}
              <button
                type="submit"
                disabled={!message.trim() || submitting}
                className="w-full mt-[14px] flex items-center justify-center bg-[#2283FF] text-white rounded-[11px] py-[13px] font-semibold text-[14px] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? t("Sending...") : t("Send request")}
              </button>
            </form>
          )}
        </div>
      </BottomSheet>
    );
  }

  // ── Desktop — artboards 1g (form, 460px) / 1h (success, 420px) ──
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0"
        style={{ background: LAW_SCRIM.modal }}
        onClick={onClose}
      />
      <div
        className={`relative bg-white rounded-[16px] shadow-[0_20px_60px_rgba(16,24,40,0.25)] w-full ${
          isSuccess ? "max-w-[420px] px-7 py-8" : "max-w-[460px] p-6"
        }`}
      >
        {isSuccess ? (
          successBody
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Header row */}
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-[11px] min-w-0">
                <Avatar
                  src={lawyer.profile_picture_url}
                  alt={lawyer.name}
                  fallbackText={lawyer.name}
                  size={38}
                  initialsSize={13}
                />
                <div className="min-w-0">
                  <h3 className="text-[15.5px] font-bold text-[#101828] truncate">
                    {t("Contact")} {lawyer.name}
                  </h3>
                  <p className="text-[12px] text-[#667085]">
                    {t("Starts a request in your TransferPitch chats")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-[2px] text-[#667085] hover:text-[#101828] cursor-pointer flex-none"
                aria-label={t("Cancel")}
              >
                <CloseIcon size={15} />
              </button>
            </div>

            {formFields}

            {/* Actions */}
            <div className="flex justify-end gap-[9px] mt-4">
              <button
                type="button"
                onClick={onClose}
                className="bg-white text-[#344054] border border-[#DDE3EC] rounded-[9px] px-4 py-[9px] font-semibold text-[13px] cursor-pointer"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={!message.trim() || submitting}
                className="bg-[#2283FF] hover:bg-[#1570E8] text-white rounded-[9px] px-[18px] py-[9px] font-semibold text-[13px] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? t("Sending...") : t("Send request")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactLawyerModal;
